import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  addDoc,
  collection,
  doc,
  DocumentData,
  Firestore,
  getDocs,
  limit,
  orderBy,
  query,
  Query,
  QuerySnapshot,
  setDoc,
  Timestamp,
  where,
} from '@angular/fire/firestore';
import axios from 'axios';
import { NGXLogger } from 'ngx-logger';
import { from, map, Observable } from 'rxjs'; // Required for modern Angular async/await
import { environment } from '../../environments/environment';
import { Album } from '../models/album.model';
import { AppExceptionModel } from '../models/app-exception.model';
import { AlbumImage } from '../models/image.album.model';
import { AuthService } from './auth.service';
import { UtilsService } from './utils.service';
@Injectable({
  providedIn: 'root',
})
export class AlbumService {
  http = inject(HttpClient);
  logger: NGXLogger = inject(NGXLogger);
  afs = inject(Firestore);
  utilsService = inject(UtilsService);
  authService = inject(AuthService);

  constructor() {}

  getAlbums({
    groupId,
    lastAlbumDate,
    limitRecords,
  }: {
    groupId: string;
    lastAlbumDate?: Timestamp;
    limitRecords?: number;
  }): Observable<Album[]> {
    this.logger.debug(
      `AlbumService:: getAlbums: groupId=${groupId}, lastAlbumDate=${lastAlbumDate}, limitRecords=${limitRecords}`,
    );
    try {
      const albumsRef = collection(this.afs, 'albums');

      let q: Query<DocumentData> = query(albumsRef);
      q = query(q, where('groupId', '==', groupId));
      if (lastAlbumDate) {
        q = query(q, where('albumDate', '<', lastAlbumDate));
      }
      q = query(q, orderBy('albumDate', 'desc'), limit(limitRecords ?? 10));

      return from(getDocs(q)).pipe(
        map((snapshot: QuerySnapshot<DocumentData>) => {
          const result: any[] = [];
          return snapshot.docs.map((doc) => {
            const album = doc.data() as Album;
            album.id = doc.id;
            return album;
          });
        }),
      );
    } catch (error: any) {
      this.logger.error(`Error fetching albums: ${JSON.stringify(error)}`);
      throw {
        code: 'ERR_FETCH_ALBUMS',
        title: 'Error Fetching Albums',
        message: error.error?.message || 'Error fetching albums',
      } as AppExceptionModel;
    }
  }

  getAlbumImages({
    groupId,
    albumId,
    lastRecord,
    limitRecords,
  }: {
    groupId: string;
    albumId: string;
    lastRecord?: Timestamp;
    limitRecords?: number;
  }): Observable<AlbumImage[]> {
    this.logger.debug(
      `AlbumService:: getAlbumImages: groupId=${groupId}, albumId=${albumId}, lastRecord=${lastRecord}, limitRecords=${limitRecords}`,
    );
    try {
      const albumsRef = collection(this.afs, 'albums', albumId, 'images');

      let q: Query<DocumentData> = query(albumsRef);
      q = query(q, where('groupId', '==', groupId));
      if (lastRecord) {
        q = query(q, where('created', '<', lastRecord));
      }
      q = query(q, orderBy('created', 'desc'), limit(limitRecords ?? 10));

      return from(getDocs(q)).pipe(
        map((snapshot: QuerySnapshot<DocumentData>) => {
          const result: any[] = [];
          return snapshot.docs.map((doc) => {
            const albumImage = doc.data() as AlbumImage;
            albumImage.id = doc.id;
            return albumImage;
          });
        }),
      );
    } catch (error: any) {
      this.logger.error(
        `Error fetching album images: ${JSON.stringify(error)}`,
      );
      throw {
        code: 'ERR_FETCH_ALBUMIMAGES',
        title: 'Error Fetching Album Images',
        message: error.error?.message || 'Error fetching album images',
      } as AppExceptionModel;
    }
  }

  async create(album: Partial<Album>): Promise<Album> {
    this.logger.log('AlbumService:: create:', album);
    const snapshotRef = collection(this.afs, 'albums');
    const result = await addDoc(snapshotRef, album);
    return { id: result.id, ...album } as Album;
  }

  async update(id: string, albumData: Partial<Album>): Promise<Album> {
    this.logger.log('AlbumService:: update:', id, albumData);
    const snapshotRef = doc(this.afs, 'albums', id);
    const result = await setDoc(snapshotRef, albumData, { merge: true });
    return { id: id, ...albumData } as Album;
  }

  async deleteImage({
    groupId,
    albumId,
    imageId,
  }: {
    groupId: string;
    albumId: string;
    imageId: string;
  }): Promise<any> {
    this.logger.log(
      `AlbumService:: deleteImage: groupId=${groupId}, albumId=${albumId}, imageId=${imageId}`,
    );
    return this.utilsService
      .setAxiosHeaders(true, 'DELETE', environment['AWS_ALBUMS_API_KEY'])
      .then(async (headers) => {
        return axios
          .delete(environment['AWS_ALBUM_IMAGE_DELETE']!, {
            params: { groupId: groupId, albumId: albumId, imageId: imageId },
            headers: headers,
          })
          .catch((error) => {
            throw error;
          })
          .then((response) => {
            if (response.status === 200) {
              return response.data;
            }
          });
      });
  }
}
